import { isValue, isWild, type Card } from "../types/card";
import type {
  EngineEvent,
  GameState,
  MeteorStrike,
  StackIndex,
  StarfallEventKind,
  StarfallState,
  StormShape,
  TableStack,
} from "../types/game";
import { STACK_INDICES, STARFALL_EVENT_KINDS } from "../types/game";
import { normalizeHouseRules, starfallApplies } from "./houseRules";

/**
 * Constellations - the table events house rule (docs/EVENTS.md). The code
 * keeps the rule's first name, Starfall: the flag, the state and the event
 * types are stored and sent under it.
 *
 * Three or more matching TOP cards light a star, and so do four in a row (a
 * staircase); a full meter draws an event off
 * a small shuffled deck and fires it as the last step of the turn that filled
 * it. This file is the rule's arithmetic - charging, drawing, the storm's
 * redeal, the bounty's pile - and holds no turn logic: the events that move
 * cards out of a deck (comets, bounty cards) need the move code's wild and
 * going-out handling, so they live in `moves.ts` and call in here.
 *
 * Randomness is carried IN the state (`StarfallState.rng`, mulberry32 stepped in
 * place) because a move is a pure function of the state it is handed - there is
 * no generator to pass `applyMove`. The Go engine steps the identical generator
 * (`starfall.go`), so a seed means the same thing in both.
 *
 * Mirrors services/game-server/internal/engine/starfall.go.
 */

/** Stars that fill the meter. A starting point, meant to be tuned through play. */
export const STARFALL_METER_SIZE = 3;
/** Tops in a row that make a staircase. Three in a row is on the table about one look in five - too cheap. */
export const STAIRCASE_LENGTH = 4;
/** Cards a Comet Shower sends from its player's deck. */
export const COMET_CARDS = 3;
/** The fewest turns a Bounty Pile burns for, so a small table still gets a lap worth racing. */
export const BOUNTY_MIN_TURNS = 3;
/** Piles one Bounty Pile event lights. Two, so the race has somewhere else to go when a pile turns bad. */
export const BOUNTY_PILES = 2;
/** Meteors in a Meteor Strike, each on a different pile. The last one to land is the big one. */
export const METEORS = 3;
/** Shards a Meteor Swarm's card breaks into, each one a meteor of its own. */
export const SWARM_SHARDS = 7;
/**
 * The most events one turn can fire. A turn can light several stars (a Five Out
 * on a Bounty Pile lays nine cards), and a Comet Shower can light more; the cap
 * keeps one turn from becoming a minute of fireworks. Stars past it wait on the
 * meter for the next turn to end.
 */
export const MAX_STARFALL_EVENTS_PER_TURN = 2;

/**
 * The generator's state after one draw, and the draw: mulberry32, as `makeRng`.
 * Takes anything carrying the state, so the Mercy Rule steps its own with it.
 */
export function nextStarfallRandom(sf: { rng: number }): number {
  let a = sf.rng | 0;
  a = (a + 0x6d2b79f5) | 0;
  sf.rng = a >>> 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

export function starfallInt(sf: { rng: number }, maxExclusive: number): number {
  return Math.floor(nextStarfallRandom(sf) * maxExclusive);
}

/** Fisher-Yates from the top, the same walk as `shuffle` in rng.ts, on the Starfall generator. */
export function starfallShuffle<T>(sf: { rng: number }, items: readonly T[]): T[] {
  const arr = items.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = starfallInt(sf, i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** The charge level of every value on these tops: max(0, count - 2), indexed by value. */
export function chargeLevels(stacks: readonly TableStack[]): number[] {
  const counts = new Array<number>(14).fill(0);
  for (const stack of stacks) {
    const top = stack.cards[0];
    if (top && isValue(top)) counts[top.value]++;
  }
  return counts.map((c) => Math.max(0, c - 2));
}

/**
 * The staircase on these tops: the values of a run of `STAIRCASE_LENGTH` or
 * more consecutive values showing, ascending, or none. Five tops can hold only
 * one such run. A value showing twice counts once, and a King does not wrap
 * round to an Ace.
 */
export function staircaseValues(stacks: readonly TableStack[]): number[] {
  const showing = new Array<boolean>(15).fill(false);
  for (const stack of stacks) {
    const top = stack.cards[0];
    if (top && isValue(top)) showing[top.value] = true;
  }
  let run: number[] = [];
  for (let value = 1; value <= 14; value++) {
    if (showing[value]) {
      run.push(value);
    } else {
      if (run.length >= STAIRCASE_LENGTH) return run;
      run = [];
    }
  }
  return [];
}

/**
 * Stars a staircase lights as the tops go from showing `before` to showing
 * `now`: one for every step past the third that was not already standing.
 *
 * So four in a row appearing lights one and a fifth joining lights another,
 * as a fourth matching top does. A staircase that SLIDES (3-4-5-6 to 4-5-6-7)
 * lights one, since a new four stands; one that merely shrinks (five down to
 * four) lights nothing, since all of what is left was there already.
 */
export function staircaseGain(before: readonly number[], now: readonly number[]): number {
  const kept = now.filter((value) => before.includes(value)).length;
  return Math.max(0, now.length - Math.max(STAIRCASE_LENGTH - 1, kept));
}

/** A fresh Starfall state for a round being dealt, its baseline taken from the dealt tops. */
export function newStarfallState(seed: number, stacks: readonly TableStack[]): StarfallState {
  return {
    stars: 0,
    bounty: null,
    levels: chargeLevels(stacks),
    stair: staircaseValues(stacks),
    deck: [],
    last: null,
    rng: seed >>> 0,
  };
}

/**
 * The Starfall state if the rule is in force at this table, else null.
 *
 * Three things must hold: the flag, a deck big enough to make a triplet, and
 * the state itself. The last is what keeps hand-built states out of it - a test
 * board or the tutorial does not have to remember to switch Starfall off,
 * because a state nobody dealt with it has nothing to charge.
 */
export function activeStarfall(state: GameState): StarfallState | null {
  const sf = state.starfall;
  if (!sf) return null;
  if (!starfallApplies(normalizeHouseRules(state.houseRules))) return null;
  return sf;
}

/**
 * Brings the meter up to date with the table's tops, lighting a star for every
 * charge level that went up since it last looked, and for every new step of a
 * staircase.
 *
 * Called after every card that comes to REST and every effect that changes a
 * top (a strike, a swap, a take). It is a diff against the last look, so a call
 * made late only charges late, never twice, and a triplet that is broken and
 * re-formed charges again because its level went down in between.
 */
export function chargeStarfall(state: GameState, playerId: string, events: EngineEvent[]): void {
  const sf = activeStarfall(state);
  if (!sf) return;
  const now = chargeLevels(state.stacks);
  for (let value = 1; value <= 13; value++) {
    const gained = now[value] - (sf.levels[value] ?? 0);
    for (let i = 0; i < gained; i++) {
      sf.stars++;
      const stackIndices = STACK_INDICES.filter((idx) => {
        const top = state.stacks[idx].cards[0];
        return top !== undefined && isValue(top) && top.value === value;
      });
      events.push({ type: "starfall-charge", playerId, value, stackIndices, stars: sf.stars });
    }
  }
  sf.levels = now;

  // The staircase: the same diff, against the run last seen. Its piles are
  // listed LOWEST VALUE FIRST, one per step, which is the order they light in.
  const stair = staircaseValues(state.stacks);
  const gained = staircaseGain(sf.stair ?? [], stair);
  for (let i = 0; i < gained; i++) {
    sf.stars++;
    const stackIndices = stair.map(
      (value) =>
        STACK_INDICES.find((idx) => {
          const top = state.stacks[idx].cards[0];
          return top !== undefined && isValue(top) && top.value === value;
        })!,
    );
    events.push({ type: "starfall-charge", playerId, value: stair[0], stackIndices, stars: sf.stars, pattern: "staircase" });
  }
  sf.stair = stair;
}

/** Takes the table's tops as the new baseline without lighting anything (after a storm). */
export function rebaseStarfall(state: GameState): void {
  const sf = activeStarfall(state);
  if (!sf) return;
  sf.levels = chargeLevels(state.stacks);
  sf.stair = staircaseValues(state.stacks);
}

/**
 * The next event off the deck, reshuffling when it runs out.
 *
 * Drawn WITHOUT replacement so a round sees variety rather than three storms,
 * and a reshuffle that would deal the event just drawn again straight away swaps
 * it one place down - so the same event never fires twice running.
 */
export function drawStarfallEvent(sf: StarfallState, kinds: readonly StarfallEventKind[] = STARFALL_EVENT_KINDS): StarfallEventKind {
  // A deck shuffled under other rules (a staged board, a save from before an
  // event was switched off) still never deals an event this table has off.
  while (sf.deck.length > 0 && !kinds.includes(sf.deck[0])) sf.deck.shift();
  if (sf.deck.length === 0) {
    const fresh = starfallShuffle(sf, kinds);
    if (fresh.length > 1 && fresh[0] === sf.last) [fresh[0], fresh[1]] = [fresh[1], fresh[0]];
    sf.deck = fresh;
  }
  const next = sf.deck.shift()!;
  sf.last = next;
  return next;
}

export const STORM_SHAPES: StormShape[] = ["even", "scattered", "tower"];

/** Splits `total` into `parts` non-negative counts, every split equally likely (stars and bars). */
function scatter(sf: StarfallState, total: number, parts: number): number[] {
  if (parts <= 1) return [total];
  // Choose parts-1 distinct bar positions among total+parts-1 slots.
  const slots = total + parts - 1;
  const positions = starfallShuffle(
    sf,
    Array.from({ length: slots }, (_, i) => i),
  )
    .slice(0, parts - 1)
    .sort((a, b) => a - b);
  const sizes: number[] = [];
  let prev = -1;
  for (const p of positions) {
    sizes.push(p - prev - 1);
    prev = p;
  }
  sizes.push(slots - prev - 1);
  return sizes;
}

/**
 * How many value cards each pile gets from a storm redealing `valueCount` of them.
 *
 * Every pile gets one first (when there are five to go round), then the rest go
 * by the storm's SHAPE, drawn with equal odds. The shape is the point: dealing
 * cards one at a time to random piles almost always comes out close to even,
 * which is not what a storm should feel like.
 *
 * - `even`: as close to equal as the count allows, extras on random piles.
 * - `scattered`: every split equally likely - usually a mix of tall and short.
 * - `tower`: one random pile takes half the rest (rounded up), the others
 *   scatter the remainder.
 *
 * Fewer than five cards: one each on that many random piles, the rest empty.
 */
export function stormSizes(sf: StarfallState, valueCount: number, shape: StormShape): number[] {
  const sizes = [0, 0, 0, 0, 0];
  if (valueCount < STACK_INDICES.length) {
    const piles = starfallShuffle(sf, STACK_INDICES).slice(0, valueCount);
    for (const p of piles) sizes[p] = 1;
    return sizes;
  }
  const rest = valueCount - STACK_INDICES.length;
  const extra = (() => {
    switch (shape) {
      case "even": {
        const each = Math.floor(rest / 5);
        const out = [each, each, each, each, each];
        for (const p of starfallShuffle(sf, STACK_INDICES).slice(0, rest % 5)) out[p]++;
        return out;
      }
      case "scattered":
        return scatter(sf, rest, 5);
      case "tower": {
        const tower = starfallInt(sf, 5);
        const tall = Math.ceil(rest / 2);
        const others = scatter(sf, rest - tall, 4);
        const out: number[] = [];
        for (let i = 0, o = 0; i < 5; i++) out.push(i === tower ? tall : others[o++]);
        return out;
      }
    }
  })();
  return sizes.map((_, i) => 1 + extra[i]);
}

/**
 * Card Storm: every card on the table is swept up and redealt. Mutates the
 * stacks and returns the shape drawn.
 *
 * Value cards are shuffled together and dealt by `stormSizes`. Wilds SINK: each
 * goes to the bottom of a random pile, because a wild on top is a free
 * placement and a storm must not hand one to whoever plays next.
 *
 * The new tops never charge the meter - the caller rebases it - or an event
 * could fire the next one.
 */
export function redealStorm(sf: StarfallState, stacks: TableStack[]): StormShape {
  const all: Card[] = stacks.flatMap((s) => s.cards);
  const values = starfallShuffle(
    sf,
    all.filter((c) => !isWild(c)),
  );
  const wilds = all.filter(isWild);
  const shape = STORM_SHAPES[starfallInt(sf, STORM_SHAPES.length)];
  const sizes = stormSizes(sf, values.length, shape);
  let at = 0;
  for (const idx of STACK_INDICES) {
    stacks[idx].cards = values.slice(at, at + sizes[idx]);
    at += sizes[idx];
  }
  for (const wild of wilds) stacks[starfallInt(sf, 5)].cards.push(wild);
  return shape;
}

/**
 * The piles a Bounty Pile event lights, `BOUNTY_PILES` of them, ascending.
 * Random piles showing a value card (the only kind a guess can be made on)
 * first; when there are too few of those, the rest come from piles with cards,
 * then from any pile.
 */
export function pickBountyStacks(sf: StarfallState, stacks: readonly TableStack[]): StackIndex[] {
  const showingValue = STACK_INDICES.filter((i) => {
    const top = stacks[i].cards[0];
    return top !== undefined && isValue(top);
  });
  const withCards = STACK_INDICES.filter((i) => stacks[i].cards.length > 0 && !showingValue.includes(i));
  const empty = STACK_INDICES.filter((i) => stacks[i].cards.length === 0);
  const picked: StackIndex[] = [];
  for (const pool of [showingValue, withCards, empty]) {
    if (picked.length >= BOUNTY_PILES) break;
    picked.push(...starfallShuffle(sf, pool).slice(0, BOUNTY_PILES - picked.length));
  }
  return picked.sort((a, b) => a - b);
}

/**
 * The piles a Meteor Strike hits, `METEORS` different ones, in the order the
 * meteors land - so the big one's pile is LAST.
 *
 * Random piles holding any card first, since those are the piles a meteor can
 * take something from - wilds included, since a meteor burns them too - then
 * empty ones. The first pile picked is the surest to have something on it, and
 * that is the one the big meteor gets: a Smash-sized impact on bare felt is the
 * one way this event could fall flat.
 */
export function pickMeteorStacks(sf: StarfallState, stacks: readonly TableStack[]): StackIndex[] {
  const holding = STACK_INDICES.filter((i) => stacks[i].cards.length > 0);
  const empty = STACK_INDICES.filter((i) => stacks[i].cards.length === 0);
  const picked: StackIndex[] = [];
  for (const pool of [holding, empty]) {
    if (picked.length >= METEORS) break;
    picked.push(...starfallShuffle(sf, pool).slice(0, METEORS - picked.length));
  }
  return [...picked.slice(1), picked[0]];
}

/**
 * Meteor Strike: three meteors on three different piles. Mutates the stacks and
 * returns what each meteor did, in landing order.
 *
 * The first two burn the top card off their pile, wild or not; the last is the
 * big one and destroys EVERY card on its pile, wilds included. Meteors are the
 * one thing that can take a wild off the table - a played wild still cannot
 * (see "A wild is never destroyed"). `resisted` is never set any more; it stays
 * on the type for events recorded before.
 *
 * The tops it exposes never charge the meter - the caller rebases it, as after
 * a storm - or an event could fire the next one.
 */
export function meteorStrike(sf: StarfallState, stacks: TableStack[]): MeteorStrike[] {
  const piles = pickMeteorStacks(sf, stacks);
  return piles.map((stackIndex, n) => {
    const big = n === piles.length - 1;
    const stack = stacks[stackIndex];
    const before = stack.cards.length;
    const top = stack.cards[0];
    let burned: Card | null = null;
    if (big) {
      stack.cards = [];
      burned = top ?? null;
    } else if (top) {
      stack.cards.shift();
      burned = top;
    }
    return {
      stackIndex,
      big,
      burned,
      destroyed: before - stack.cards.length,
      top: stack.cards[0] ?? null,
      count: stack.cards.length,
    };
  });
}

/** One small meteor on `stack`: its top card burns off, wild or not. */
function burnTopOf(stack: TableStack, stackIndex: StackIndex): MeteorStrike {
  const top = stack.cards[0];
  const strike: MeteorStrike = { stackIndex, big: false, burned: null, destroyed: 0, top: top ?? null, count: stack.cards.length };
  if (!top) return strike;
  stack.cards.shift();
  return { ...strike, burned: top, destroyed: 1, top: stack.cards[0] ?? null, count: stack.cards.length };
}

/**
 * Meteor Swarm: `SWARM_SHARDS` small meteors, one after another, round every
 * pile that has cards. Mutates the stacks and returns what each did, in order.
 *
 * The piles holding cards are put in a random order and the shards go round
 * it, skipping any pile a shard before has emptied, so every pile with cards
 * is hit before any is hit twice - and a table with one pile of cards takes
 * all seven there. Each burns the top card off as a Meteor Strike's small
 * meteor does, wild or not. Once nothing on the table can be hit, the rest
 * land on bare felt: a random pile, which is empty by then.
 *
 * The tops it exposes never charge the meter - the caller rebases it.
 */
export function meteorSwarm(sf: StarfallState, stacks: TableStack[]): MeteorStrike[] {
  const order = starfallShuffle(
    sf,
    STACK_INDICES.filter((i) => stacks[i].cards.length > 0),
  );
  const strikes: MeteorStrike[] = [];
  let cursor = 0;
  for (let n = 0; n < SWARM_SHARDS; n++) {
    let stackIndex: StackIndex | null = null;
    for (let step = 0; step < order.length; step++) {
      const at = (cursor + step) % order.length;
      if (stacks[order[at]].cards.length > 0) {
        stackIndex = order[at];
        cursor = (at + 1) % order.length;
        break;
      }
    }
    if (stackIndex === null) stackIndex = STACK_INDICES[starfallInt(sf, STACK_INDICES.length)];
    strikes.push(burnTopOf(stacks[stackIndex], stackIndex));
  }
  return strikes;
}

/** Turns a bounty lit now burns for: a lap of the players still holding cards, at least `BOUNTY_MIN_TURNS`. */
export function bountyTurns(state: GameState): number {
  const holding = state.players.filter((p) => p.deck.length > 0).length;
  return Math.max(holding, BOUNTY_MIN_TURNS);
}

/** True while `stackIndex` is one of the Bounty Piles at a table playing Starfall. */
export function isBountyStack(state: GameState, stackIndex: StackIndex): boolean {
  const sf = activeStarfall(state);
  return !!sf && sf.bounty !== null && sf.bounty.stackIndices.includes(stackIndex);
}

/** Card counts of every pile with its top, for the `card-storm` event. */
export function stormLayout(stacks: readonly TableStack[]): { count: number; top: Card | null }[] {
  return stacks.map((s) => ({ count: s.cards.length, top: s.cards[0] ?? null }));
}
